import React, { useState } from 'react';
import { Button } from '../ui/button';
import { 
  CheckSquare, 
  Square, 
  Eye, 
  EyeOff, 
  RotateCw, 
  Trash2, 
  AlertTriangle,
  CheckCircle,
  X,
  Package,
  Loader2
} from 'lucide-react';
import { toast } from 'react-toastify';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

export const BulkPlanOperations = ({ 
  selectedPlans, 
  onSelectionChange, 
  onOperationComplete,
  plans = [] 
}) => {
  const [isPerforming, setIsPerforming] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [pendingOperation, setPendingOperation] = useState(null);

  const bulkOperations = [
    {
      id: 'enable',
      label: 'Enable Plans',
      icon: Eye,
      description: 'Make selected plans public and available for subscription',
      confirmMessage: 'This will make the selected plans publicly available.',
      dangerLevel: 'low'
    },
    {
      id: 'disable', 
      label: 'Disable Plans',
      icon: EyeOff,
      description: 'Hide selected plans from public view (existing subscriptions continue)',
      confirmMessage: 'This will hide the selected plans from new subscriptions.',
      dangerLevel: 'medium'
    },
    {
      id: 'sync_stripe',
      label: 'Sync to Stripe',
      icon: RotateCw,
      description: 'Synchronize selected plans with Stripe (create/update products)',
      confirmMessage: 'This will sync the selected plans to Stripe. This may create new products.',
      dangerLevel: 'low'
    }
  ];

  const handleSelectAll = () => {
    if (selectedPlans.length === plans.length) {
      onSelectionChange([]);
    } else {
      onSelectionChange(plans.map(plan => plan.id));
    }
  };

  const handleOperationClick = (operation) => {
    if (selectedPlans.length === 0) {
      toast.error('Please select at least one plan');
      return;
    }
    
    setPendingOperation(operation);
    setShowConfirmation(true);
  };

  const handleConfirmOperation = async () => {
    if (!pendingOperation || selectedPlans.length === 0) return;

    setIsPerforming(true);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/admin/monetization/plans/bulk`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          plan_ids: selectedPlans,
          operation: pendingOperation.id,
          justification: `Bulk ${pendingOperation.label.toLowerCase()} operation on ${selectedPlans.length} plans`,
          impact_assessment: `${pendingOperation.description} Applied to ${selectedPlans.length} plans.`
        })
      });

      if (response.ok) {
        const result = await response.json();
        
        if (result.success_count > 0) {
          toast.success(
            `Successfully ${pendingOperation.label.toLowerCase()}d ${result.success_count} plan(s)`
          );
        }
        
        if (result.error_count > 0) {
          toast.warning(
            `Failed to process ${result.error_count} plan(s). Check the results for details.`
          );
        }
        
        // Reset selection and refresh data
        onSelectionChange([]);
        onOperationComplete(result);
        
      } else {
        const errorData = await response.json();
        toast.error(errorData.detail || 'Bulk operation failed');
      }
    } catch (err) {
      console.error('Bulk operation error:', err);
      toast.error('Failed to perform bulk operation');
    } finally {
      setIsPerforming(false);
      setShowConfirmation(false);
      setPendingOperation(null);
    }
  };

  const selectedPlanNames = plans
    .filter(plan => selectedPlans.includes(plan.id))
    .map(plan => plan.name);

  return (
    <>
      {/* Bulk Operations Bar */}
      {selectedPlans.length > 0 && (
        <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 mb-4 dark:bg-indigo-900/10 dark:border-indigo-800">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <CheckSquare className="w-5 h-5 text-indigo-600" />
              <div>
                <div className="font-medium text-indigo-900 dark:text-indigo-100">
                  {selectedPlans.length} plan{selectedPlans.length > 1 ? 's' : ''} selected
                </div>
                <div className="text-sm text-indigo-700 dark:text-indigo-300">
                  {selectedPlanNames.slice(0, 3).join(', ')}
                  {selectedPlanNames.length > 3 && ` and ${selectedPlanNames.length - 3} more`}
                </div>
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {bulkOperations.map(operation => {
                const Icon = operation.icon;
                return (
                  <Button
                    key={operation.id}
                    onClick={() => handleOperationClick(operation)}
                    variant="outline"
                    size="sm"
                    disabled={isPerforming}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {operation.label}
                  </Button>
                );
              })}
              
              <Button
                onClick={() => onSelectionChange([])}
                variant="outline"
                size="sm"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Select All Checkbox */}
      <div className="flex items-center gap-3 mb-4">
        <label className="flex items-center">
          <input
            type="checkbox"
            checked={selectedPlans.length === plans.length && plans.length > 0}
            onChange={handleSelectAll}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="ml-2 text-sm text-gray-700 dark:text-gray-300">
            Select All ({plans.length} plans)
          </span>
        </label>
      </div>

      {/* Confirmation Modal */}
      {showConfirmation && pendingOperation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full dark:bg-gray-900">
            <div className="flex items-center gap-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-yellow-600" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Confirm Bulk Operation
              </h3>
            </div>
            
            <div className="mb-4">
              <p className="text-gray-600 dark:text-gray-300 mb-3">
                You are about to <strong>{pendingOperation.label.toLowerCase()}</strong> {selectedPlans.length} plan(s):
              </p>
              
              <div className="bg-gray-50 rounded-lg p-3 mb-3 dark:bg-gray-800">
                <div className="text-sm space-y-1">
                  {selectedPlanNames.map(name => (
                    <div key={name} className="flex items-center gap-2">
                      <Package className="w-3 h-3 text-gray-400" />
                      <span>{name}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-300">
                {pendingOperation.confirmMessage}
              </p>
            </div>
            
            <div className="flex justify-end gap-3">
              <Button 
                onClick={() => setShowConfirmation(false)} 
                variant="outline"
                disabled={isPerforming}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleConfirmOperation}
                disabled={isPerforming}
                className={pendingOperation.dangerLevel === 'high' ? 'bg-red-600 hover:bg-red-700' : ''}
              >
                {isPerforming ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Confirm {pendingOperation.label}
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default BulkPlanOperations;